import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import LoadingCover from 'src/components/LoadingCover';
import { IPublicTrackingExpired, IPublicTrackingResult, lookupPublicTracking, normalizeWalId, PUBLIC_TRACKING_STATUS, readExpiredDetail, WAL_ID_REGEX } from 'src/services/PublicTrackingService';

import SharedPageTopBar from '../SharedQuote/SharedPageTopBar';
import ShareRadarOrder from '../ShareRadarOrder';

import PublicTrackingGlobe from './PublicTrackingGlobe';
import PublicTrackingSearch, { PublicTrackingNotice } from './PublicTrackingSearch';

/**
 * Public tracking page — track.westernalliancelogistics.com (spec: docs lucas/2026-10-01/public-tracking-page/).
 *
 * Before a search: the portal's public top bar with the WALHQ search in the middle, over the whole
 * globe. The WAL ID is typed in the bar; a panel drops down under it, explaining every miss with a
 * call to a rep. On a hit the match is shown as a result row and then the live tracking page renders
 * in place, with everything its tracking link shows, for every status (Lucas, 2026-10-01): a booked
 * shipment opens on its pickup schedule, a delivered one on its delivery schedule.
 *
 * The WAL ID is kept in ?walId= so a refresh, or a link to this page, shows the same shipment.
 *
 * OPENING A MATCH (client, 2026-10-07: "on the search when id matches should show and smooth
 * transition"). A hit used to replace the globe with the tracking page in the same render — two
 * different trees swapped at once, which read as a jump. It now runs as a short sequence:
 *
 *   found    the result row sits in the panel for RESULT_DWELL_MS, so the match is seen
 *   opening  the globe fades out over FADE_MS; the top bar and the search never move
 *   idle     the shipment takes its place, showing its own loading cover until it has painted
 *            (ShareRadarOrder isFirstPaintDone) — without that, the fade landed on a page still
 *            assembling itself, which is what it was supposed to hide
 *
 * Clicking the row (or pressing Enter) skips the dwell, and a deep link skips it too — nobody typed
 * anything there, so there is no match to confirm.
 *
 * ARRIVING WITH ?walId= ALREADY SET is its own case. The lookup starts before anything is on screen,
 * and the globe is the wrong thing to show while it runs: the visitor asked for one shipment, and a
 * spinning earth says the page is idle. So a cover holds over the frame until the lookup answers —
 * a hit opens the shipment straight through it, a miss clears it to reveal the globe with the
 * "we couldn't find that shipment" panel already open under the search.
 *
 * ONE COVER, NOT TWO (client, 2026-10-07: "on landing there is 2 loading screens"). A deep link runs
 * one wait — look the ID up, then load the shipment and draw its route — but it crosses a component
 * boundary halfway through, and the two halves each used to raise and clear their own cover. The
 * visitor saw a spinner, saw it fade to an empty frame, then saw a second spinner with different
 * wording. So the arrival hit now does NOT fade: it mounts the shipment in the same update, behind a
 * cover built from the same component (LoadingCover) and carrying the same label, so what is on
 * screen does not change across the swap. Only ShareRadarOrder's copy of it ever fades out, once,
 * onto the loaded shipment. The dwell-and-fade sequence above still applies to a TYPED search, where
 * there is a match to confirm and a globe to leave.
 */
// How long the result row sits before the shipment opens on its own. Long enough to read the WAL ID
// and the route, short enough that nobody waiting on it reaches for the mouse.
const RESULT_DWELL_MS = 800;
// One fade, both ways. Matches the panel's own transitions; longer starts to feel like latency.
const FADE_MS = 300;

/** idle = globe or shipment at rest. found and opening are the opening sequence, in order. */
type TransitionPhase = 'idle' | 'found' | 'opening';

const PublicTracking = () => {
    const { t } = useTranslation();
    const [searchParams, setSearchParams] = useSearchParams();
    const [walIdInput, setWalIdInput] = useState(() => searchParams.get('walId') ?? '');
    const [isSearching, setIsSearching] = useState(false);
    const [notice, setNotice] = useState<PublicTrackingNotice | null>(null);
    // The 410 body behind an 'expired' notice (WAL ID + delivery date). Kept apart from `notice` so
    // the notice stays a plain union and every other state keeps rendering from i18n alone.
    const [expired, setExpired] = useState<IPublicTrackingExpired | null>(null);
    const [shown, setShown] = useState<{ walId: string; link: IPublicTrackingResult['link'] } | null>(null);
    // The match being shown in the panel, and where the opening sequence has got to.
    const [result, setResult] = useState<IPublicTrackingResult | null>(null);
    const [phase, setPhase] = useState<TransitionPhase>('idle');
    // Cover for the ?walId= arrival (see the header). Initialised from the URL on the very first
    // render, not in an effect, so the globe is never painted for a frame before the cover lands.
    // up -> fading -> gone, the same three steps ShareRadarOrder's first-paint cover uses.
    const [arrivalCover, setArrivalCover] = useState<'up' | 'fading' | 'gone'>(() => (searchParams.get('walId') ? 'up' : 'gone'));

    useEffect(() => {
        document.title = t('PublicTracking.Page_Title') as string;
    }, [t]);

    const search = useCallback(
        // openImmediately: a deep link (?walId=) has no typed input to confirm, so it skips the row.
        async (raw: string, openImmediately = false): Promise<boolean> => {
            const walId = normalizeWalId(raw);

            // Never send a malformed ID: it cannot match, and a miss counts against the visitor's IP.
            if (!WAL_ID_REGEX.test(walId)) {
                setExpired(null);
                setNotice('invalid');

                return false;
            }

            setExpired(null);
            setNotice(null);
            setIsSearching(true);

            try {
                const data = await lookupPublicTracking(walId);
                setSearchParams({ walId }, { replace: true });
                setWalIdInput(walId);

                // Deep link: hand the wait straight over. The arrival cover is up, ShareRadarOrder
                // comes up behind an identical one, and these updates are batched into a single
                // render — so the swap happens under an unbroken cover. Running the fade here
                // instead would clear the cover to an empty frame and raise a second one, which is
                // the double loading screen the client reported.
                if (openImmediately) {
                    setShown({ walId: data.orderId, link: data.link });
                    setResult(null);
                    setPhase('idle');

                    return true;
                }

                // Typed search: the shipment is not mounted here — `result` puts the match in the
                // panel and the sequence below opens it, so the globe is never yanked out from
                // under the visitor.
                setResult(data);
                setPhase('found');

                return true;
            } catch (error: any) {
                const status = error?.response?.status;
                const isExpired = status === PUBLIC_TRACKING_STATUS.EXPIRED;

                // Only the expired branch carries a body worth reading; the rest are deliberately
                // identical, so they are told apart by status code alone.
                setExpired(isExpired ? readExpiredDetail(error, walId) : null);
                setNotice(
                    status === PUBLIC_TRACKING_STATUS.INVALID
                        ? 'invalid'
                        : status === PUBLIC_TRACKING_STATUS.NOT_FOUND
                        ? 'notFound'
                        : isExpired
                        ? 'expired'
                        : status === PUBLIC_TRACKING_STATUS.TOO_MANY
                        ? 'blocked'
                        : 'error'
                );

                // A miss never replaces the shipment already on screen, nor an opening sequence
                // already under way; the panel explains it.
                setResult(null);
                setPhase('idle');

                return false;
            } finally {
                setIsSearching(false);
            }
        },
        [setSearchParams]
    );

    // Deep link / refresh: search the ID in the URL once on arrival. The ref keeps it to one lookup
    // when React runs mount effects twice in development (each lookup counts against the IP).
    const hasAutoSearchedRef = useRef(false);

    useEffect(() => {
        const initialWalId = searchParams.get('walId');
        if (!initialWalId || hasAutoSearchedRef.current) return;
        hasAutoSearchedRef.current = true;

        // On a hit the cover is never cleared here: `search` mounts the shipment under it and
        // ShareRadarOrder's own cover takes over in the same frame, so there is nothing to fade.
        // Only a miss clears it, revealing the globe with the notice already open.
        search(initialWalId, true).then((isHit) => {
            if (!isHit) setArrivalCover('fading');
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // fading -> gone: dropped only after the fade has run, so it cannot cut its own transition short.
    useEffect(() => {
        if (arrivalCover !== 'fading') return undefined;

        const timer = window.setTimeout(() => setArrivalCover('gone'), FADE_MS);

        return () => window.clearTimeout(timer);
    }, [arrivalCover]);

    // Opens the match now — the row was clicked, or Enter was pressed on it.
    const openNow = useCallback(() => setPhase((current) => (current === 'found' ? 'opening' : current)), []);

    // found -> opening: the dwell that lets the match be read.
    useEffect(() => {
        if (phase !== 'found') return undefined;

        const timer = window.setTimeout(() => setPhase('opening'), RESULT_DWELL_MS);

        return () => window.clearTimeout(timer);
    }, [phase]);

    // opening -> idle: once the body has faded out, swap it for the shipment. It comes up showing
    // its own first-paint cover (ShareRadarOrder isFirstPaintDone) in the same frame the globe just
    // left, so it is shown straight away rather than faded in again — the globe dissolves into the
    // loading frame, and the cover dissolves into the loaded shipment. Mounting waits until here
    // because ShareRadarOrder pulls the detail and a Mapbox canvas up with it, and doing that behind
    // a still-visible globe stutters the fade on a slow machine.
    useEffect(() => {
        if (phase !== 'opening' || !result) return undefined;

        const timer = window.setTimeout(() => {
            setShown({ walId: result.orderId, link: result.link });
            setResult(null);
            setPhase('idle');
        }, FADE_MS);

        return () => window.clearTimeout(timer);
    }, [phase, result]);

    // The body is hidden only while it fades out. Applied to the shipment too, so searching a second
    // WAL ID from the tracking page fades the old one away before the new one takes its place.
    const bodyTransitionClass = `transition-opacity ease-out ${phase === 'opening' ? 'opacity-0' : 'opacity-100'}`;

    // The label the whole wait is shown under, from the first frame of the arrival cover through to
    // the shipment's own. One wording, so the handoff between the two components reads as one screen.
    const loadingLabel = t('PublicTracking.Looking_Up', { walId: shown?.walId || walIdInput || searchParams.get('walId') }) as string;

    const searchBar = (
        <PublicTrackingSearch
            value={walIdInput}
            onChange={(value) => {
                setWalIdInput(value);

                if (notice) {
                    setExpired(null);
                    setNotice(null);
                }
            }}
            onSubmit={() => search(walIdInput)}
            notice={notice}
            expired={expired}
            isSearching={isSearching}
            result={result}
            onOpen={openNow}
            isOpening={phase === 'opening'}
        />
    );

    // Keyed by token: each search mounts the tracking page fresh, exactly as opening its link would.
    // The wrapper carries the fade; it cannot live inside ShareRadarOrder, which draws its own top
    // bar — fading that would move the search the visitor is still looking at.
    if (shown) {
        const publicTracking = { token: shown.link.token, searchContent: searchBar, loadingLabel };

        return (
            <div className={`h-full ${bodyTransitionClass}`} style={{ transitionDuration: `${FADE_MS}ms` }}>
                <ShareRadarOrder key={shown.link.token} publicTracking={publicTracking} />
            </div>
        );
    }

    return (
        <main className="h-screen flex flex-col bg-utilityBlack700">
            <h1 className="sr-only">{t('PublicTracking.Page_Title')}</h1>
            <SharedPageTopBar centerContent={searchBar} />
            {/* Same frame as the tracking link page. */}
            <div className="flex-1 flex flex-col min-h-0 lg:overflow-hidden w-full mx-auto lg:px-4 rounded-t-xl global-background-color">
                <div className="flex flex-col flex-1 min-h-0 max-w-[1480px] w-full mx-auto lg:rounded-[20px] lg:overflow-hidden lg:p-5">
                    {/* The frame itself never fades — only what is inside it — and it is WHITE, not
                        transparent. Fading the whole frame used to leave the page background showing
                        through for the length of the transition, so a typed search went globe ->
                        empty box -> loading screen. Now the globe dissolves into the same white the
                        shipment's cover is drawn on, and the bar above never moves either way. */}
                    <section className="flex-1 relative overflow-hidden bg-white lg:rounded-[20px] shadow-[0px_0px_12px_0px_rgba(16,24,40,0.08),0px_4px_12px_-2px_rgba(16,24,40,0.10)]">
                        <div className={`h-full ${bodyTransitionClass}`} style={{ transitionDuration: `${FADE_MS}ms` }}>
                            <PublicTrackingGlobe />
                        </div>

                        {/* Arrival cover (see the header). Sits over the globe rather than replacing
                            it so the canvas starts its loop underneath and is already running when
                            the cover clears on a miss. Same component the shipment covers itself
                            with, which is what makes the handoff on a hit invisible. */}
                        {arrivalCover !== 'gone' && <LoadingCover label={loadingLabel} phase={arrivalCover} fadeMs={FADE_MS} className="z-[2]" />}
                    </section>
                </div>
            </div>
        </main>
    );
};

export default PublicTracking;
